import { db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  serverTimestamp,
  onSnapshot,
  deleteField,
  deleteDoc,
  runTransaction,
} from 'firebase/firestore';

const generateInviteCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export const ROLES = [
  { value: 'speaker1', label: 'Speaker 1', icon: '🗣️', desc: 'Formuli gli indizi per primo' },
  { value: 'speaker2', label: 'Speaker 2', icon: '🎙️', desc: 'Formuli gli indizi per secondo' },
  { value: 'guesser', label: 'Guesser', icon: '🎯', desc: 'Ascolta e indovina la parola' },
];

export const ROLE_LABELS = {
  speaker1: 'Speaker 1',
  speaker2: 'Speaker 2',
  guesser: 'Guesser',
};

export const teamService = {
  /**
   * Crea una nuova squadra. Il creatore sceglie subito il proprio ruolo permanente.
   */
  createTeam: async (name, userId, userName, role) => {
    const id = `team_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const inviteCode = generateInviteCode();
    await setDoc(doc(db, 'teams', id), {
      id,
      name,
      inviteCode,
      createdBy: userId,
      createdAt: serverTimestamp(),
      members: [{ userId, name: userName, role: role || null, joinedAt: Date.now() }],
      memberIds: [userId],
      studyProgress: {
        wordCoverage: 0,
        progress: { speaker1: 0, speaker2: 0, guesser: 0 },
      },
    });
    return id;
  },

  /**
   * Unisce un utente a una squadra tramite codice invito.
   * Valida che il ruolo scelto non sia già occupato.
   */
  joinTeamByCode: async (inviteCode, userId, userName, role) => {
    const q = query(collection(db, 'teams'), where('inviteCode', '==', inviteCode.toUpperCase()));
    const snap = await getDocs(q);
    if (snap.empty) throw new Error('Codice squadra non trovato');
    const teamDoc = snap.docs[0];
    const data = teamDoc.data();

    // Già dentro
    if (data.members.find((m) => m.userId === userId)) return { id: teamDoc.id, ...data };
    if (data.members.length >= 3) throw new Error('La squadra è già completa (max 3 membri)');

    // Controllo unicità ruolo
    if (role && data.members.find((m) => m.role === role)) {
      throw new Error(`Il ruolo ${ROLE_LABELS[role]} è già occupato in questa squadra`);
    }

    await updateDoc(doc(db, 'teams', teamDoc.id), {
      members: arrayUnion({ userId, name: userName, role: role || null, joinedAt: Date.now() }),
      memberIds: arrayUnion(userId),
    });
    return { id: teamDoc.id, ...data };
  },

  getTeam: async (teamId) => {
    const snap = await getDoc(doc(db, 'teams', teamId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  /**
   * Recupera tutti i team di cui l'utente fa parte (come creatore O come membro).
   * Usa array-contains su userId.
   */
  getTeamsByUser: async (userId) => {
    // Firestore non supporta array-contains su oggetti, quindi usiamo createdBy + query separata
    // Strategia: query per createdBy, poi query per tutti i team e filtra client-side
    // (efficiente per dataset piccoli come team personali)
    const allSnap = await getDocs(
      query(collection(db, 'teams'), where('createdBy', '==', userId)),
    );
    const createdTeams = allSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Cerca anche team in cui l'utente è membro ma non creatore
    // (Firestore non supporta array-contains su campi annidati, quindi fetch all + filter)
    // Per evitare full-collection scan, usiamo un campo separato memberIds se disponibile
    // Fallback: lista già caricata dai team creati è sufficiente per ora,
    // ma aggiungiamo un campo 'memberIds' al momento del join per query efficienti
    return createdTeams;
  },

  /**
   * Recupera team dove l'utente è membro (via memberIds array).
   * Se memberIds non esiste sul documento, fa fallback su client-side filter.
   */
  getAllTeamsByUser: async (userId) => {
    // Query su memberIds (array semplice di uid)
    const q = query(collection(db, 'teams'), where('memberIds', 'array-contains', userId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  assignRole: async (teamId, userId, role) => {
    const ref = doc(db, 'teams', teamId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();

    // Verifica unicità ruolo
    const alreadyTaken = data.members.find((m) => m.userId !== userId && m.role === role);
    if (alreadyTaken) throw new Error(`Il ruolo ${ROLE_LABELS[role]} è già occupato`);

    const members = data.members.map((m) => (m.userId === userId ? { ...m, role } : m));
    await updateDoc(ref, { members });
  },

  updateStudyProgress: async (teamId, role, pctValue) => {
    const ref = doc(db, 'teams', teamId);
    // Use dot notation to update a nested field without overwriting the whole object
    await updateDoc(ref, {
      [`studyProgress.progress.${role}`]: pctValue,
    });
  },

  leaveTeam: async (teamId, userId) => {
    const ref = doc(db, 'teams', teamId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const members = data.members.filter((m) => m.userId !== userId);
    const memberIds = (data.memberIds || []).filter((id) => id !== userId);
    
    if (members.length === 0) {
      await deleteDoc(ref);
    } else {
      await updateDoc(ref, { members, memberIds });
    }
  },

  /**
   * Listener real-time sul documento del team.
   * Ritorna la funzione di unsubscribe.
   */
  subscribeToTeam: (teamId, callback) => {
    const ref = doc(db, 'teams', teamId);
    return onSnapshot(ref, (snap) => {
      if (snap.exists()) callback({ id: snap.id, ...snap.data() });
    });
  },

  /**
   * Imposta una sessione di gioco attiva nel team (per accesso senza codice).
   */
  setActiveSession: async (teamId, sessionData) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, {
      activeSession: {
        ...sessionData,
        startedAt: serverTimestamp(),
      },
    });
  },

  /**
   * Avvia la sessione di allenamento multiplayer: scrive il gameState condiviso nel team.
   * Tutti i client in ascolto sul team verranno reindirizzati automaticamente.
   * @param {string} teamId
   * @param {Object[]} words - Array di parole { id, text }
   * @param {string} startedBy - UID di chi ha avviato
   */
  startTrainingGame: async (teamId, words, startedBy) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, {
      'trainingSession.gameState': {
        status: 'countdown',
        currentWordIndex: 0,
        words,
        score: 0,
        wordsGuessed: 0,
        wrongAnswers: 0,
        countdownStartedAt: Date.now(),
        timerRemaining: 0,
        timerRunning: false,
        pausedAt: null,
        startedBy,
      },
    });
  },

  /**
   * Aggiorna lo stato condiviso del gioco di allenamento.
   * @param {string} teamId
   * @param {Object} updates
   */
  updateTrainingGameState: async (teamId, updates) => {
    const ref = doc(db, 'teams', teamId);
    // Prefisso tutti i campi con trainingSession.gameState.*
    const prefixed = {};
    Object.entries(updates).forEach(([k, v]) => {
      prefixed[`trainingSession.gameState.${k}`] = v;
    });
    await updateDoc(ref, prefixed);
  },

  /**
   * Rimuove il gameState della sessione di allenamento (fine partita).
   * @param {string} teamId
   */
  clearTrainingGame: async (teamId) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, {
      'trainingSession.gameState': deleteField(),
    });
  },

  /**
   * Atomic two-phase claim for a multiplayer session.
   * Prevents race conditions when multiple members click simultaneously.
   *
   * Phase 1: read team atomically; if activeSession exists, verify lobby is alive.
   * Phase 2: if no valid session, create a new lobby outside the transaction,
   *          then atomically set activeSession only if no one else got there first.
   *          If someone won the race, delete our tentative lobby and join theirs.
   *
   * @param {string} teamId
   * @param {string} uid
   * @param {Function} createRoomFn   - lobbyService.createRoom(uid, name)
   * @param {Function} checkRoomFn   - lobbyService.roomExists(code)
   * @param {string}   displayName
   * @returns {string} The authoritative lobby code to join
   */
  claimMultiplayerSession: async (teamId, uid, createRoomFn, checkRoomFn, displayName) => {
    const teamRef = doc(db, 'teams', teamId);

    // ── Phase 1: check for an existing, living session ──────────
    const teamSnap = await getDoc(teamRef);
    if (!teamSnap.exists()) throw new Error('Squadra non trovata');
    const existing = teamSnap.data().activeSession;

    if (existing?.type === 'multiplayer' && existing?.lobbyCode) {
      const alive = await checkRoomFn(existing.lobbyCode);
      if (alive) return existing.lobbyCode; // happy path – already a lobby, join it
      // Lobby is gone (stale) – fall through to create a new one
    }

    // ── Phase 2: create a tentative lobby, then claim atomically ─
    const tentativeCode = await createRoomFn(uid, displayName);
    let finalCode = tentativeCode;
    let weWon = false;

    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(teamRef);
        if (!snap.exists()) throw new Error('Squadra non trovata');
        const data = snap.data();

        if (data.activeSession?.type === 'multiplayer' && data.activeSession?.lobbyCode) {
          // Someone else won the race while we were creating our lobby
          finalCode = data.activeSession.lobbyCode;
          weWon = false;
        } else {
          // We are first – claim the session
          tx.update(teamRef, {
            activeSession: {
              type: 'multiplayer',
              lobbyCode: tentativeCode,
              startedBy: displayName || uid,
              startedAt: serverTimestamp(),
            },
          });
          finalCode = tentativeCode;
          weWon = true;
        }
      });
    } catch (err) {
      // Transaction failed – clean up and rethrow
      await deleteDoc(doc(db, 'lobbies', tentativeCode)).catch(() => {});
      throw err;
    }

    if (!weWon) {
      // Delete the unused tentative lobby silently
      await deleteDoc(doc(db, 'lobbies', tentativeCode)).catch(() => {});

      // Verify the winner's lobby still exists (edge case: it disappeared too)
      const alive = await checkRoomFn(finalCode);
      if (!alive) {
        // Recurse once – the other lobby died between the transaction and now
        return teamService.claimMultiplayerSession(teamId, uid, createRoomFn, checkRoomFn, displayName);
      }
    }

    return finalCode;
  },

  /**
   * Rimuove la sessione attiva dal team.
   */
  clearActiveSession: async (teamId) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, { activeSession: null });
  },

  /**
   * Entra nella sessione di allenamento del team.
   * Usa un oggetto-mappa { uid: role } per evitare array con serverTimestamp.
   */
  joinTrainingSession: async (teamId, userId, role) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, {
      [`trainingSession.members.${userId}`]: role || 'unknown',
    });
  },

  /**
   * Esce dalla sessione di allenamento del team.
   */
  leaveTrainingSession: async (teamId, userId) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, {
      [`trainingSession.members.${userId}`]: deleteField(),
    });
  },

  /**
   * Imposta il set di parole scelto per la sessione di allenamento.
   * Tutti i presenti vedono la modifica in real-time.
   */
  setTrainingWordSet: async (teamId, limit) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, {
      'trainingSession.wordSet': limit,
    });
  },

  /**
   * Aggiorna il progresso studio di un membro nel team.
   * @param {string} teamId
   * @param {string} role - 'speaker1' | 'speaker2' | 'guesser'
   * @param {number} pct - percentuale 0-100
   */
  updateStudyProgress: async (teamId, role, pct) => {
    const ref = doc(db, 'teams', teamId);
    await updateDoc(ref, {
      [`studyProgress.progress.${role}`]: pct,
    });
  },

  /**
   * Ritorna i ruoli ancora disponibili in un team (non assegnati).
   */
  getAvailableRoles: (team) => {
    const takenRoles = new Set((team.members || []).map((m) => m.role).filter(Boolean));
    return ROLES.filter((r) => !takenRoles.has(r.value));
  },

  /**
   * Ritorna il ruolo dell'utente in un team, o null.
   */
  getUserRole: (team, userId) => {
    const member = (team.members || []).find((m) => m.userId === userId);
    return member?.role || null;
  },
};
